import { useTranslation } from 'react-i18next';
import { Button, Empty, EmptyHeader, EmptyTitle, Spinner } from '@monksflow/monks-ui';
import { Plus, Radio } from 'lucide-react';
import type { ConnectorPlatform } from '@/entities/media-connector';
import { AddAccountSheet, EditAccountDialog } from '@/features/media-connectors';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal/DeleteConfirmationModal';
import { AccountsTable, MediaConnectorToolbar } from '@/pages/MediaConnectors/components';
import { useMediaConnectorAvailability } from '@/pages/MediaConnectors/model/useMediaConnectorAvailability';
import { useMediaConnectorDetail } from '@/pages/MediaConnectors/model/useMediaConnectorDetail';
import type { MediaConnectorPaneProps } from './MediaConnectorPane.types';

/**
 * Detail pane for the active platform, beside the `NavigationPanel` that picks
 * it. Stays
 * on screen (as an `Empty` state, not a redirect) when nothing is selected or
 * the selected platform can't be shown, so the rail remains available to pick
 * another one rather than the page navigating away under the user.
 *
 * Only the guards live here, and they run on `useMediaConnectorAvailability`,
 * which queries the platform list and nothing else. The account list and the
 * 30s sync-status poll belong to `useMediaConnectorDetail`, which is mounted
 * by the inner component below — so sitting on `/knowledge-hub/media` with no
 * platform selected does not start polling a detail view that is not on screen.
 */
export function MediaConnectorPane({ platform }: MediaConnectorPaneProps) {
  const { t } = useTranslation('knowledge');
  const availability = useMediaConnectorAvailability(platform);

  if (platform === null) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('mediaConnectors.mediaConnectorPane.selectAPlatform')}</EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  }

  // Unknown and flag-disabled platforms render identically — a disabled platform must
  // never leak that it exists just because it's an otherwise-valid platform name.
  if (!availability.isValidPlatform) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('mediaConnectors.mediaConnectorPane.platformUnavailable')}</EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  }

  // Wait for the enabled-platform list before deciding — avoids flashing the
  // unavailable state while it loads.
  if (availability.platformsPending) {
    return (
      <div className="flex flex-1 items-center justify-center rounded-lg bg-card p-xl text-foreground-alt">
        <Spinner className="size-5" />
      </div>
    );
  }

  if (!availability.isPlatformEnabled) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('mediaConnectors.mediaConnectorPane.platformUnavailable')}</EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  }

  return <MediaConnectorPaneContent platform={platform as ConnectorPlatform} />;
}

MediaConnectorPane.displayName = 'MediaConnectorPane';

interface MediaConnectorPaneContentProps {
  platform: ConnectorPlatform;
}

/**
 * The pane body, mounted only once the platform is known to be valid and
 * enabled. Everything expensive — the account list, the mutations and the
 * sync-status poll — hangs off the hook called here, so it starts when there
 * is something to show it for and stops when there is not.
 */
function MediaConnectorPaneContent({ platform }: MediaConnectorPaneContentProps) {
  const { t } = useTranslation('knowledge');
  const { t: tCommon } = useTranslation('common');
  const detail = useMediaConnectorDetail(platform);

  return (
    <div
      data-testid="media-connector-pane"
      className="flex min-h-0 flex-1 flex-col gap-xl rounded-lg bg-card p-xl"
    >
      <div className="flex items-center gap-3">
        <img src={detail.platformIcon} alt="" className="size-5 object-contain" />
        <h2 className="text-lg font-medium text-foreground">{detail.platformLabel}</h2>
      </div>

      {!detail.isPending && detail.accounts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Radio className="mb-4 size-12 text-muted-foreground/40" />
          <h3 className="mb-1 text-lg font-medium text-foreground">
            {t('mediaConnectors.mediaConnectorDetail.noAccountsConnected')}
          </h3>
          <p className="mb-6 max-w-[24rem] text-sm text-foreground-alt">
            {t('mediaConnectors.mediaConnectorDetail.connectYourFirstPlatformAccount', {
              platform: detail.platformLabel,
            })}
          </p>
          <Button variant="default" size="sm" onClick={() => detail.setDialogOpen(true)}>
            <Plus className="mr-1.5 size-4" />
            {t('mediaConnectors.mediaConnectorDetail.addAccount')}
          </Button>
        </div>
      ) : (
        <>
          <MediaConnectorToolbar
            search={detail.search}
            onSearchChange={detail.setSearch}
            statusFilter={detail.statusFilter}
            onStatusFilterChange={detail.setStatusFilter}
            onAddAccount={() => detail.setDialogOpen(true)}
            isConnecting={detail.connectingPlatform === detail.platform}
          />

          <AccountsTable
            accounts={detail.filteredAccounts}
            syncStatusMap={detail.syncStatusMap}
            isBusy={detail.isBusy}
            onEdit={detail.setEditingAccount}
            onDelete={detail.setDeletingAccount}
            onToggle={detail.handleToggle}
            onSync={detail.handleSync}
            onTest={detail.handleTest}
            testingAccountId={detail.testingAccountId}
            togglingAccountId={detail.togglingAccountId}
            syncingAccountId={detail.syncingAccountId}
          />
        </>
      )}

      <AddAccountSheet
        open={detail.dialogOpen}
        onOpenChange={detail.setDialogOpen}
        platformLabel={detail.platformLabel}
        platform={detail.platform}
        isSubmitting={detail.isConnectSubmitting}
        onSubmit={detail.handleAddAccount}
      />

      <EditAccountDialog
        open={detail.editingAccount !== null}
        onOpenChange={(open) => {
          if (!open) detail.setEditingAccount(null);
        }}
        initialTitle={detail.editingAccount?.title ?? ''}
        initialTags={detail.editingAccount?.tags ?? []}
        initialSyncCadence={detail.editingAccount?.syncCadence ?? 'daily'}
        initialDsaBeneficiary={detail.editingAccount?.dsaBeneficiary ?? ''}
        initialDsaPayor={detail.editingAccount?.dsaPayor ?? ''}
        connectorId={detail.editingAccount?.id ?? null}
        initialAdvertiserId={detail.editingAccount?.advertiserId ?? null}
        initialAdvertiserName={detail.editingAccount?.advertiserName ?? null}
        onSubmit={detail.handleEditSubmit}
        loading={detail.isUpdating}
      />

      <DeleteConfirmationModal
        open={detail.deletingAccount !== null}
        onCancel={() => detail.setDeletingAccount(null)}
        onConfirmDelete={detail.handleDeleteConfirm}
        isDeleting={detail.isDisconnecting}
        resourceType={tCommon('components.deleteConfirmationModal.account')}
        resourceName={
          detail.deletingAccount?.title ||
          detail.deletingAccount?.externalAccountName ||
          t('mediaConnectors.mediaConnectorDetail.thisAccount')
        }
        warningMessage={t('mediaConnectors.mediaConnectorDetail.thisWillDisconnectTheAccount')}
      />
    </div>
  );
}

MediaConnectorPaneContent.displayName = 'MediaConnectorPaneContent';
